import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../../../appContext";
import { IFileProvider } from "../../../../api/fileProvider/IFileProvider";
import { MultiFileProvider } from "../../../../api/fileProvider/multiFileProvider";
import { AnalysisProvider } from "../../../../api/fileProvider/analysisProvider";
import { IAnalysisFolder } from "../../../dashboardPage/components/fullAnalysis/IAnalysisFolder";
import { FullAnalysisFolder } from "../../../dashboardPage/components/fullAnalysis/FullAnalysisFolder";
import { FullAnalysisFolderSettings } from "../../../dashboardPage/components/fullAnalysis/FullAnalysisFolderSettings";
import { FullAnalysisCrumbs } from "../../../dashboardPage/components/fullAnalysis/FullAnalysisCrumbs";
import { FullAnalysisFile } from "../../../dashboardPage/components/fullAnalysis/FullAnalysisFile";
import { IPlanarFolder } from "../../../dashboardPage/components/fullAnalysis/FSPlanarize";
import { IFullAnalysisFileRecord } from "../../../dashboardPage/components/fullAnalysis/FullAnalysisTypes";
import { ConfirmationPopup } from "../../../../components/confirmationPopup/ConfirmationPopup";
import './MeetingPageAnalysisWidget.css';

const filterFileForClient = (file: IPlanarFolder, clientId: string): IPlanarFolder => {
    const result: IFullAnalysisFileRecord[] = [];
    if (file.fileData && Array.isArray(file.fileData)) {
        file.fileData.forEach(record => {
            if (record.relatedClientId == clientId) {
                result.push(record);
            }
        });
    }
    return {
        ...file,
        fileData: result
    };
};

interface IMeetingPageAnalysisWidgetProps {
    selectedClientId?: string;
    selectedClientName?: string;
}

export const MeetingPageAnalysisWidget = ({ selectedClientId, selectedClientName }: IMeetingPageAnalysisWidgetProps) => {
    const { translate } = useContext(AppContext);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [fileProvider, setFileProvider] = useState<IFileProvider | null>(null);
    const [selectedForEdit, setSelectedForEdit] = useState<IAnalysisFolder | null>(null);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState<IAnalysisFolder | null>(null);
    const [selectedFileToView, setSelectedFileToView] = useState<IAnalysisFolder | null>(null);
    const [filterByClient, setFilterByClient] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isOperating, setIsOperating] = useState(false);
    const [fix, setFix] = useState(0);

    useEffect(() => {
        const provider = new MultiFileProvider(AnalysisProvider);
        provider.onChange = () => {
            setIsLoading(false);
            setFix(last => last + 1);
        };
        provider.onAuthError = () => {
            console.error('Auth error in analysis widget');
            setIsLoading(false);
        };
        
        setFileProvider(provider);
    }, []);

    useEffect(() => {
        if (!fileProvider) return;
        
        setIsLoading(true);
        fileProvider.getFiles()
            .catch(err => {
                console.error('Failed to load files:', err);
                setIsLoading(false);
            });
    }, [fileProvider]);

    const crumbs = fileProvider?.crumbs || [];
    const currentFolder = crumbs[crumbs.length - 1];
    const rawFolders = currentFolder?.folderItems || [];
    const showBreadcrumbs = crumbs.length > 1;

    const folders = filterByClient && selectedClientId
        ? rawFolders.map(folder => {
            if (folder.type === 'file' && folder.fileData) {
                return filterFileForClient(folder as unknown as IPlanarFolder, selectedClientId);
            }
            return folder;
        }).filter(folder => {
            if (folder.type === 'file' && folder.fileData && Array.isArray(folder.fileData)) {
                return folder.fileData.length > 0;
            }
            return true;
        })
        : rawFolders;

    const handleSelect = async (folder: IAnalysisFolder | IPlanarFolder) => {
        if (isOperating) return;
        if (folder.type === 'folder' && folder.id) {
            setIsOperating(true);
            try {
                await fileProvider?.getFiles(folder.id);
            } finally {
                setIsOperating(false);
            }
        } else if (folder.type === 'file') {
            setSelectedFileToView(folder as IAnalysisFolder);
        }
    };

    const handleRename = async (item: IAnalysisFolder, newName: string) => {
        if (!item.id || isOperating) return;
        setIsOperating(true);
        try {
            await fileProvider?.updateFile({ ...item, name: newName });
        } finally {
            setIsOperating(false);
        }
    };

    const handleDelete = (item: IAnalysisFolder) => {
        if (!item.id || isOperating) return;
        setDeleteConfirmItem(item);
    };

    const confirmDelete = async () => {
        if (deleteConfirmItem?.id) {
            setIsOperating(true);
            setDeleteConfirmItem(null);
            try {
                await fileProvider?.deleteFile(deleteConfirmItem.id);
            } finally {
                setIsOperating(false);
            }
        }
    };

    const handleBreadcrumb = async (folder: IAnalysisFolder) => {
        if (isOperating) return;
        setIsOperating(true);
        try {
            await fileProvider?.getFiles(folder.id, folder.name);
        } finally {
            setIsOperating(false);
        }
    };

    const handleSettings = (item: IAnalysisFolder) => {
        if (isOperating) return;
        setSelectedForEdit(item);
    };

    const handleAddFolder = async () => {
        if (!fileProvider || isOperating) return;

        setIsOperating(true);
        try {
            const newFolder: IAnalysisFolder = {
                name: translate('analysis_new_folder') || 'New Folder',
                iconIndex: 0,
                date: new Date(),
                type: 'folder',
                folderItems: []
            };

            const parentId = currentFolder?.id || '';
            await fileProvider.createFolder(newFolder, parentId);
        } finally {
            setIsOperating(false);
        }
    };

    if (!selectedClientId) {
        return (
            <div className={`meetingPageAnalysis ${isCollapsed ? 'meetingPageAnalysis--collapsed' : ''}`}>
                <div className="meetingPageAnalysis_header" onClick={() => setIsCollapsed(!isCollapsed)}>
                    <div className="meetingPageAnalysis_title_wrapper">
                        <div className="meetingPageAnalysis_title">{translate('analysis_title') || 'Analysis'}</div>
                    </div>
                    <div className="meetingPageAnalysis_toggle">
                        <div className="meetingPageAnalysis_toggle_icon"></div>
                    </div>
                </div>
                <div className="meetingPageAnalysis_drop">
                    <div className="meetingPageAnalysis_drop_content">
                        <div className="meetingPageAnalysis_empty">
                            {translate('analysis_select_client') || 'Select a client to view analysis'}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`meetingPageAnalysis ${isCollapsed ? 'meetingPageAnalysis--collapsed' : ''}`}>
            <div className="meetingPageAnalysis_header" onClick={() => setIsCollapsed(!isCollapsed)}>
                <div className="meetingPageAnalysis_title_wrapper">
                    <div className="meetingPageAnalysis_title">{translate('analysis_title') || 'Analysis'}</div>
                    <div 
                        className={`meetingPageAnalysis_filter_icon ${!filterByClient ? 'meetingPageAnalysis_filter_icon--active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setFilterByClient(!filterByClient);
                        }}
                        title={filterByClient 
                            ? translate('filter_show_client_files')?.replace('{name}', selectedClientName || '')
                            : translate('filter_show_all_files')
                        }
                    />
                </div>
                <div className="meetingPageAnalysis_toggle">
                    <div className="meetingPageAnalysis_toggle_icon"></div>
                </div>
            </div>
            
            <div className="meetingPageAnalysis_drop">
                <div className="meetingPageAnalysis_drop_content">
                    {isOperating && (
                        <div className="meetingPageAnalysis_overlay">
                            <div className="meetingPageAnalysis_spinner"></div>
                        </div>
                    )}
                    
                    {showBreadcrumbs && (
                        <div className="meetingPageAnalysis_breadcrumbs">
                            <FullAnalysisCrumbs
                                crumbs={crumbs}
                                onCrumb={handleBreadcrumb}
                            />
                        </div>
                    )}
                    
                    {isLoading ? (
                        <div className="meetingPageAnalysis_loading">{translate('common_loading') || 'Loading...'}</div>
                    ) : (
                        <div className="meetingPageAnalysis_content">
                            <div className="meetingPageAnalysis_folders">
                                {folders.length === 0 && (
                                    <div className="meetingPageAnalysis_empty_folder">
                                        {translate('analysis_empty_folder') || 'No files or folders yet'}
                                    </div>
                                )}
                                {folders.slice(0, 6).map((folder) => (
                                    <FullAnalysisFolder
                                        key={folder.id || folder.name}
                                        folder={folder as IAnalysisFolder}
                                        className="meetingPageAnalysis_folder_item"
                                        onSelect={() => handleSelect(folder)}
                                        onRename={(newName) => handleRename(folder as IAnalysisFolder, newName)}
                                        onDelete={(item) => handleDelete(item)}
                                        onSettings={(item) => handleSettings(item)}
                                    />
                                ))}
                            </div>
                            <div className="meetingPageAnalysis_actions">
                                <div className={`meetingPageAnalysis_add_wrapper ${isOperating ? 'meetingPageAnalysis_add_wrapper--disabled' : ''}`} onClick={handleAddFolder}>
                                    <div className="meetingPageAnalysis_add_icon"></div>
                                    <span className="meetingPageAnalysis_add_text">{translate('analysis_add_folder') || 'Add a new folder'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {selectedForEdit && (
                <FullAnalysisFolderSettings
                    folder={selectedForEdit}
                    onClose={() => setSelectedForEdit(null)}
                    onSubmit={async (data) => {
                        setSelectedForEdit(null);
                        setIsOperating(true);
                        try {
                            await fileProvider?.updateFile({ ...selectedForEdit, ...data });
                        } finally {
                            setIsOperating(false);
                        }
                    }}
                />
            )}

            {deleteConfirmItem && (
                <ConfirmationPopup
                    message={`${translate('analysis_confirm_delete')} "${deleteConfirmItem.name}"?`}
                    confirmText={translate('common_delete') || 'Delete'}
                    cancelText={translate('common_cancel') || 'Cancel'}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteConfirmItem(null)}
                    confirmButtonStyle="danger"
                />
            )}

            {selectedFileToView && (
                <div className="meetingPageAnalysis_file_popup_overlay" onClick={() => setSelectedFileToView(null)}>
                    <div className="meetingPageAnalysis_file_popup_content" onClick={(e) => e.stopPropagation()}>
                        <div className="meetingPageAnalysis_file_popup_header">
                            <div className="meetingPageAnalysis_file_popup_title">{selectedFileToView.name}</div>
                            <button className="meetingPageAnalysis_file_popup_close" onClick={() => setSelectedFileToView(null)}>
                                ✕
                            </button>
                        </div>
                        <div className="meetingPageAnalysis_file_popup_body">
                            <FullAnalysisFile file={selectedFileToView} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
